import { CHARACTER_ORDER, CHARACTER_TYPES, type CharacterKind } from '../config/characterConfig';
import { LEVELS, type LevelDefinition } from '../config/levelConfig';
import { TOOL_CONFIG, TOOL_ORDER, type ToolId } from '../config/toolConfig';
import type { ScoreResult } from '../systems/ScoreSystem';
import type { RescueStatistics } from '../systems/StatisticsSystem';

export interface HudData {
  stamina: number;
  distance: number;
  riverSpeed: number;
  timeLeft: number;
  selectedTool: ToolId | null;
  attempt: number;
  characterName: string;
}

export interface ResultViewData {
  success: boolean;
  title: string;
  copy: string;
  quip: string;
  tool: ToolId | 'observe';
  elapsed: number;
  stamina: number;
  score: ScoreResult;
  seed: number;
  secondaryAttempts: number;
}

export interface CustomSettings {
  character: CharacterKind | 'random';
  riverSpeed: number;
  wind: number;
  obstacles: number;
  rescueTime: number;
  strict: boolean;
  allowSwitching: boolean;
  showTrajectory: boolean;
  funnyFailure: boolean;
  tools: ToolId[];
}

type ActionHandler = (action: string, value?: string) => void;

const toolIcon: Record<ToolId, string> = {
  lifeRing: '◎',
  rope: '⌁',
  lifeVest: '▣',
  rescuePole: '╱',
  inflatableBoat: '▱',
  autoSalvage: '⌾',
};

export class UIController {
  readonly root: HTMLElement;
  private handler: ActionHandler = () => undefined;
  private toastTimer = 0;
  private power = 0;

  constructor(app: HTMLElement) {
    this.root = document.createElement('div');
    this.root.id = 'game-ui';
    this.root.innerHTML = this.template();
    app.append(this.root);
    this.root.addEventListener('click', this.onClick);
    this.root.addEventListener('submit', this.onSubmit);
  }

  private template(): string {
    const levelCards = LEVELS.map(
      (level) => `
        <button class="level-card" type="button" data-action="start-level" data-value="${level.id}">
          <span class="level-number">${String(level.index).padStart(2, '0')}</span>
          <span><b>${level.name}</b><small>${level.eyebrow}</small></span>
          <i>›</i>
        </button>
      `,
    ).join('');
    const characters = CHARACTER_ORDER.map(
      (kind) => `<option value="${kind}">${CHARACTER_TYPES[kind].name}</option>`,
    ).join('');
    const customTools = TOOL_ORDER.map(
      (id) => `
        <label class="check-chip">
          <input type="checkbox" name="tools" value="${id}" checked />
          <span>${TOOL_CONFIG[id].key} ${TOOL_CONFIG[id].name}</span>
        </label>
      `,
    ).join('');
    const tools = TOOL_ORDER.map(
      (id) => `
        <button class="tool-button" type="button" data-action="select-tool" data-value="${id}" aria-label="${TOOL_CONFIG[id].key} ${TOOL_CONFIG[id].name}">
          <kbd>${TOOL_CONFIG[id].key}</kbd>
          <span class="tool-symbol">${toolIcon[id]}</span>
          <span class="tool-copy"><b>${TOOL_CONFIG[id].name}</b><small>${TOOL_CONFIG[id].risk}</small></span>
        </button>
      `,
    ).join('');
    return `
      <div class="vignette" aria-hidden="true"></div>
      <header class="game-brand">
        <button class="brand-mark" type="button" data-action="home" aria-label="返回游戏首页">救</button>
        <span><b>救人别手抖！</b><small>Rescue Roulette</small></span>
      </header>

      <section id="menu-screen" class="screen menu-screen is-visible" aria-labelledby="game-title">
        <div class="menu-copy">
          <p class="eyebrow">A VERY SERIOUS RIVER RESCUE GAME</p>
          <h1 id="game-title">救人<br /><span>别手抖！</span></h1>
          <p class="subtitle">Rescue Roulette</p>
          <p class="lead">一个人掉进了河里。五件工具，十五秒，还有一双不太可靠的手。</p>
          <div class="menu-actions">
            <button class="primary-button jumbo" type="button" data-action="start-level" data-value="calm">
              开始救援 <span>空格</span>
            </button>
            <button class="secondary-button" type="button" data-action="open-panel" data-value="levels">选择关卡</button>
            <button class="secondary-button" type="button" data-action="open-panel" data-value="custom">自定义模式</button>
          </div>
          <div class="menu-links">
            <button type="button" data-action="open-panel" data-value="stats">本设备记录</button>
            <button type="button" data-action="open-panel" data-value="help">操作说明</button>
            <button type="button" data-action="toggle-audio">音效：开</button>
            <button type="button" data-action="toggle-quality">画质：高</button>
          </div>
        </div>
        <aside class="menu-ticket" aria-label="游戏提示">
          <span class="ticket-label">TODAY'S DUTY</span>
          <b>先看清楚，<br />再把东西扔出去。</b>
          <div class="ticket-rule"></div>
          <small>有时候“观察”才是正确答案。</small>
        </aside>
        <p class="safety-note">本游戏为卡通虚构内容。现实中遇到落水事故，请立即呼叫专业救援人员，并在确保自身安全的情况下使用正规救生设备。请勿模仿游戏操作。</p>
      </section>

      <section id="level-intro" class="level-intro" aria-live="polite">
        <p id="intro-eyebrow">第一关</p>
        <h2 id="intro-title">平静的小河</h2>
        <p id="intro-copy">水流缓慢，距离较近。</p>
      </section>

      <div id="cinema-caption" class="cinema-caption" aria-live="polite"></div>

      <section id="hud" class="hud" aria-label="救援状态">
        <div class="hud-card stamina-card">
          <span>落水者体力</span>
          <b id="stamina-value">82%</b>
          <div class="stamina-track"><i id="stamina-fill"></i></div>
        </div>
        <div class="hud-metrics">
          <div><span>距离岸边</span><b id="distance-value">14.0m</b></div>
          <div><span>水流速度</span><b id="flow-value">2.3m/s</b></div>
          <div class="time-metric"><span>剩余时间</span><b id="time-value">15.0</b></div>
        </div>
        <button class="pause-button" type="button" data-action="pause" aria-label="暂停游戏">Ⅱ</button>
      </section>

      <section id="victim-chip" class="victim-chip">
        <span class="pulse-dot"></span>
        <span>正在救援</span>
        <b id="victim-name">散步大叔</b>
        <small id="attempt-label">第 1 次尝试</small>
      </section>

      <section id="tool-panel" class="tool-panel" aria-label="选择救援工具">
        <div class="tool-panel-head">
          <span>选择救援工具</span>
          <small>按 A 到 F 快速选择</small>
        </div>
        <div class="tool-grid">${tools}</div>
        <button class="observe-button" type="button" data-action="observe">
          <span>◉</span><b>先观察</b><small>别急着添乱</small>
        </button>
      </section>

      <section id="aim-panel" class="aim-panel" aria-label="瞄准和蓄力">
        <div class="crosshair" aria-hidden="true"><i></i><i></i></div>
        <div class="aim-readout">
          <div>
            <span id="aim-tool-name">救生圈</span>
            <b id="aim-instruction">移动指针瞄准，按住蓄力</b>
          </div>
          <div class="power-wrap">
            <span>力度</span>
            <div class="power-track"><i id="power-fill"></i><em></em></div>
            <b id="power-value">0%</b>
          </div>
          <button id="throw-button" class="primary-button hold-button" type="button" data-action="hold-action">按住蓄力</button>
          <button class="quiet-button" type="button" data-action="cancel-tool">换个工具</button>
        </div>
      </section>

      <section id="interaction-panel" class="interaction-panel" aria-live="polite"></section>

      <section id="pause-panel" class="pause-panel screen">
        <div class="modal-card compact">
          <p class="eyebrow">TIME OUT</p>
          <h2>先喘口气</h2>
          <p>倒计时和河流已暂停。</p>
          <button class="primary-button" type="button" data-action="resume">继续救援</button>
          <button class="secondary-button" type="button" data-action="home">返回首页</button>
        </div>
      </section>

      <section id="result-screen" class="result-screen screen">
        <article class="result-card">
          <div class="result-grade">
            <span>救援评级</span>
            <b id="result-grade">A</b>
            <small id="result-grade-title">专业救援员</small>
          </div>
          <div class="result-main">
            <p class="eyebrow" id="result-kicker">RESCUE COMPLETE</p>
            <h2 id="result-title">人救上来了，救生圈也没丢。</h2>
            <p id="result-copy">一次有惊无险的救援。</p>
            <blockquote id="result-quip">“猫虽然获救，但它明显不感谢你。”</blockquote>
            <dl class="result-meta">
              <div><dt>最终得分</dt><dd id="result-score">900</dd></div>
              <div><dt>救援用时</dt><dd id="result-time">8.4s</dd></div>
              <div><dt>剩余体力</dt><dd id="result-stamina">61%</dd></div>
              <div><dt>使用道具</dt><dd id="result-tool">救生圈</dd></div>
            </dl>
            <div id="score-lines" class="score-lines"></div>
            <div class="result-actions">
              <button class="primary-button" type="button" data-action="replay">再次救援</button>
              <button class="secondary-button" type="button" data-action="next-level">下一关</button>
              <button class="secondary-button" type="button" data-action="home">返回首页</button>
              <button class="quiet-button" type="button" data-action="open-panel" data-value="stats">查看本设备记录</button>
            </div>
            <p class="result-safety">现实中遇到落水事故，请优先呼叫专业救援人员，不要盲目下水或模仿本游戏操作。</p>
          </div>
        </article>
      </section>

      <div id="panel-backdrop" class="panel-backdrop" hidden></div>
      <aside id="side-panel" class="side-panel" aria-hidden="true">
        <header>
          <div><p class="eyebrow" id="panel-eyebrow">MISSION SELECT</p><h2 id="panel-title">选择关卡</h2></div>
          <button type="button" data-action="close-panel" aria-label="关闭">×</button>
        </header>
        <div id="panel-content" class="panel-content">
          <div id="levels-content" class="panel-view">${levelCards}</div>
          <div id="custom-content" class="panel-view">
            <form id="custom-form" class="custom-form">
              <label><span>落水者</span><select name="character"><option value="random">完全随机</option>${characters}</select></label>
              <label><span>水流速度 <output data-output="riverSpeed">1.8</output> m/s</span><input type="range" name="riverSpeed" min="0.6" max="3.8" value="1.8" step="0.1" /></label>
              <label><span>风力 <output data-output="wind">0.6</output></span><input type="range" name="wind" min="0" max="1.8" value="0.6" step="0.1" /></label>
              <label><span>障碍物 <output data-output="obstacles">3</output> 个</span><input type="range" name="obstacles" min="0" max="10" value="3" step="1" /></label>
              <label><span>救援时间 <output data-output="rescueTime">15</output> 秒</span><input type="range" name="rescueTime" min="8" max="30" value="15" step="1" /></label>
              <fieldset><legend>可用工具</legend><div class="check-grid">${customTools}</div></fieldset>
              <div class="switch-list">
                <label><input type="checkbox" name="allowSwitching" checked /><span>允许更换工具</span></label>
                <label><input type="checkbox" name="showTrajectory" checked /><span>显示投掷轨迹</span></label>
                <label><input type="checkbox" name="funnyFailure" checked /><span>搞笑失败动画</span></label>
                <label><input type="checkbox" name="strict" /><span>严格模式</span></label>
              </div>
              <button class="primary-button" type="submit">开始自定义救援</button>
            </form>
          </div>
          <div id="stats-content" class="panel-view"></div>
          <div id="help-content" class="panel-view help-view">
            <p class="large-copy">看清距离与水流，再选工具。鼠标和触摸都能控制瞄准，长按蓄力，松手投掷。</p>
            <ol>
              <li><b>A 救生圈</b><span>瞄准落水者附近，不要正中脑袋。</span></li>
              <li><b>B 救生绳</b><span>套住腰部，在正确时机收紧并快速回拉。</span></li>
              <li><b>C 救生衣</b><span>抛到身边，再按正确顺序完成穿戴。</span></li>
              <li><b>D 救援杆</b><span>安全但长度有限，保持稳定再回拉。</span></li>
              <li><b>E 充气小船</b><span>先快速充满，再左右操舵接近目标。</span></li>
              <li><b>F 自动打捞碟群</b><span>点击即部署。无需瞄准、蓄力或手动回拉。</span></li>
            </ol>
            <p class="shortcut-copy"><kbd>A</kbd><kbd>B</kbd><kbd>C</kbd><kbd>D</kbd><kbd>E</kbd><kbd>F</kbd> 选择工具　<kbd>Space</kbd> 蓄力或操作　<kbd>P</kbd> 暂停</p>
          </div>
        </div>
        <footer>
          <button class="secondary-button" type="button" data-action="fullscreen">进入全屏</button>
          <button class="quiet-button danger-text" type="button" data-action="reset-stats">重置本地记录</button>
        </footer>
      </aside>

      <div id="toast" class="toast" role="status" aria-live="polite"></div>
      <div id="debug-panel" class="debug-panel" hidden></div>
    `;
  }

  bind(handler: ActionHandler): void {
    this.handler = handler;
  }

  private onClick = (event: Event): void => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!button) return;
    this.handler(button.dataset.action ?? '', button.dataset.value);
  };

  private onSubmit = (event: Event): void => {
    if ((event.target as HTMLFormElement).id !== 'custom-form') return;
    event.preventDefault();
    this.handler('start-custom');
  };

  bindRangeOutputs(): void {
    this.root.querySelectorAll<HTMLInputElement>('.custom-form input[type="range"]').forEach((input) => {
      input.addEventListener('input', () => {
        const output = this.root.querySelector<HTMLOutputElement>(`[data-output="${input.name}"]`);
        if (output) output.value = input.value;
      });
    });
  }

  getCustomSettings(): CustomSettings {
    const form = this.get<HTMLFormElement>('custom-form');
    const data = new FormData(form);
    return {
      character: (data.get('character') as CharacterKind | 'random') ?? 'random',
      riverSpeed: Number(data.get('riverSpeed')),
      wind: Number(data.get('wind')),
      obstacles: Number(data.get('obstacles')),
      rescueTime: Number(data.get('rescueTime')),
      strict: data.has('strict'),
      allowSwitching: data.has('allowSwitching'),
      showTrajectory: data.has('showTrajectory'),
      funnyFailure: data.has('funnyFailure'),
      tools: data.getAll('tools') as ToolId[],
    };
  }

  showMenu(): void {
    this.root.classList.remove('is-playing', 'is-result', 'is-paused');
    this.get('menu-screen').classList.add('is-visible');
    this.get('result-screen').classList.remove('is-visible');
    this.get('pause-panel').classList.remove('is-visible');
    this.setCaption('');
  }

  hideMenu(): void {
    this.root.classList.add('is-playing');
    this.get('menu-screen').classList.remove('is-visible');
  }

  showIntro(level: LevelDefinition): void {
    const intro = this.get('level-intro');
    this.get('intro-eyebrow').textContent = `第 ${level.index} 关　${level.eyebrow}`;
    this.get('intro-title').textContent = level.name;
    this.get('intro-copy').textContent = level.description;
    intro.classList.add('is-visible');
  }

  hideIntro(): void {
    this.get('level-intro').classList.remove('is-visible');
  }

  setCaption(text: string): void {
    const caption = this.get('cinema-caption');
    caption.textContent = text;
    caption.classList.toggle('is-visible', Boolean(text));
  }

  showHud(visible: boolean): void {
    this.get('hud').classList.toggle('is-visible', visible);
    this.get('victim-chip').classList.toggle('is-visible', visible);
  }

  updateHud(data: HudData): void {
    const stamina = Math.max(0, Math.round(data.stamina * 100));
    this.get('stamina-value').textContent = `${stamina}%`;
    this.get('stamina-fill').style.width = `${stamina}%`;
    this.get('stamina-fill').classList.toggle('is-danger', stamina < 28);
    this.get('distance-value').textContent = `${data.distance.toFixed(1)}m`;
    this.get('flow-value').textContent = `${data.riverSpeed.toFixed(1)}m/s`;
    this.get('time-value').textContent = data.timeLeft.toFixed(1);
    this.get('time-value').classList.toggle('is-danger', data.timeLeft <= 4);
    this.get('victim-name').textContent = data.characterName;
    this.get('attempt-label').textContent = `第 ${data.attempt} 次尝试`;
  }

  showTools(visible: boolean): void {
    this.get('tool-panel').classList.toggle('is-visible', visible);
  }

  configureTools(available: ToolId[], used: ToolId[] = []): void {
    this.root.querySelectorAll<HTMLButtonElement>('.tool-button').forEach((button) => {
      const id = button.dataset.value as ToolId;
      const isUsed = used.includes(id);
      button.disabled = !available.includes(id) || isUsed;
      button.classList.toggle('is-used', isUsed);
    });
  }

  selectTool(id: ToolId | null): void {
    this.root.querySelectorAll('.tool-button').forEach((button) => {
      button.classList.toggle('is-selected', (button as HTMLElement).dataset.value === id);
    });
  }

  showAim(id: ToolId, instruction: string): void {
    this.get('aim-panel').classList.add('is-visible');
    this.get('aim-tool-name').textContent = `${TOOL_CONFIG[id].key}　${TOOL_CONFIG[id].name}`;
    this.get('aim-instruction').textContent = instruction;
    this.setPower(0);
  }

  hideAim(): void {
    this.get('aim-panel').classList.remove('is-visible');
  }

  setPower(value: number): void {
    this.power = Math.max(0, Math.min(1, value));
    this.get('power-fill').style.width = `${Math.round(this.power * 100)}%`;
    this.get('power-value').textContent = `${Math.round(this.power * 100)}%`;
  }

  getPower(): number {
    return this.power;
  }

  setInteraction(html: string): void {
    const panel = this.get('interaction-panel');
    panel.innerHTML = html;
    panel.classList.toggle('is-visible', Boolean(html));
  }

  clearInteraction(): void {
    this.setInteraction('');
  }

  setInteractionProgress(value: number): void {
    const fill = this.root.querySelector<HTMLElement>('[data-interaction-fill]');
    if (fill) fill.style.width = `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
    const output = this.root.querySelector<HTMLElement>('[data-interaction-value]');
    if (output) output.textContent = `${Math.round(value * 100)}%`;
  }

  pause(paused: boolean): void {
    this.root.classList.toggle('is-paused', paused);
    this.get('pause-panel').classList.toggle('is-visible', paused);
  }

  showResult(data: ResultViewData): void {
    this.root.classList.add('is-result');
    const screen = this.get('result-screen');
    screen.classList.add('is-visible');
    this.get('result-grade').textContent = data.score.grade;
    this.get('result-grade-title').textContent = data.score.title;
    this.get('result-kicker').textContent = data.success ? 'RESCUE COMPLETE' : 'RESCUE FAILED';
    this.get('result-title').textContent = data.title;
    this.get('result-copy').textContent = data.copy;
    this.get('result-quip').textContent = `“${data.quip}”`;
    this.get('result-score').textContent = String(data.score.total);
    this.get('result-time').textContent = `${data.elapsed.toFixed(1)}s`;
    this.get('result-stamina').textContent = `${Math.round(data.stamina * 100)}%`;
    this.get('result-tool').textContent = data.tool === 'observe' ? '先观察' : TOOL_CONFIG[data.tool].name;
    this.get('score-lines').innerHTML = data.score.lines
      .map(
        (line) => `<div><span>${line.label}</span><b class="${line.value < 0 ? 'negative' : ''}">${line.value > 0 ? '+' : ''}${line.value}</b></div>`,
      )
      .join('');
    screen.querySelector<HTMLElement>('.result-card')?.classList.toggle('is-failure', !data.success);
  }

  hideResult(): void {
    this.root.classList.remove('is-result');
    this.get('result-screen').classList.remove('is-visible');
  }

  openPanel(panel: 'levels' | 'custom' | 'stats' | 'help', stats?: RescueStatistics): void {
    const titles = {
      levels: ['MISSION SELECT', '选择关卡'],
      custom: ['BUILD A MESS', '自定义模式'],
      stats: ['LOCAL RECORDS ONLY', '本设备救援记录'],
      help: ['HOW NOT TO PANIC', '操作说明'],
    };
    this.get('panel-eyebrow').textContent = titles[panel][0];
    this.get('panel-title').textContent = titles[panel][1];
    this.root.querySelectorAll('.panel-view').forEach((view) => view.classList.remove('is-visible'));
    this.get(`${panel}-content`).classList.add('is-visible');
    if (panel === 'stats' && stats) this.renderStats(stats);
    this.get('panel-backdrop').hidden = false;
    requestAnimationFrame(() => {
      this.get('side-panel').classList.add('is-visible');
      this.get('side-panel').setAttribute('aria-hidden', 'false');
      this.get('panel-backdrop').classList.add('is-visible');
    });
  }

  closePanel(): void {
    this.get('side-panel').classList.remove('is-visible');
    this.get('side-panel').setAttribute('aria-hidden', 'true');
    this.get('panel-backdrop').classList.remove('is-visible');
    setTimeout(() => {
      this.get('panel-backdrop').hidden = true;
    }, 260);
  }

  renderStats(stats: RescueStatistics): void {
    const average = stats.total ? stats.totalRescueTime / stats.total : 0;
    const rate = stats.total ? Math.round((stats.successes / stats.total) * 100) : 0;
    const toolRows = TOOL_ORDER.map((id) => {
      const tool = stats.toolStats[id];
      const toolRate = tool.uses ? Math.round((tool.successes / tool.uses) * 100) : 0;
      return `<div><span>${TOOL_CONFIG[id].name}<small>${tool.uses} 次使用</small></span><b>${toolRate}%</b></div>`;
    }).join('');
    this.get('stats-content').innerHTML = `
      <div class="stats-hero"><b>${stats.total}</b><span>次本设备救援</span></div>
      <div class="stats-grid">
        <div><span>成功率</span><b>${rate}%</b></div>
        <div><span>最高分</span><b>${stats.highScore}</b></div>
        <div><span>最佳连胜</span><b>${stats.bestStreak}</b></div>
        <div><span>平均用时</span><b>${average.toFixed(1)}s</b></div>
        <div><span>救过人类</span><b>${stats.humansSaved}</b></div>
        <div><span>救过动物</span><b>${stats.animalsSaved}</b></div>
      </div>
      <h3>工具表现</h3>
      <div class="tool-stats">${toolRows}</div>
      <p class="stats-foot">完美救援 ${stats.perfectRescues} 次　眩晕事故 ${stats.dazedCount} 次　绳索错误 ${stats.ropeErrors} 次　穿戴错误 ${stats.vestErrors} 次</p>
      <p class="stats-foot muted">没有后端，没有伪造的全球玩家数据。</p>
    `;
  }

  toast(message: string, tone: 'normal' | 'warning' | 'success' = 'normal'): void {
    const toast = this.get('toast');
    toast.textContent = message;
    toast.dataset.tone = tone;
    toast.classList.add('is-visible');
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2400);
  }

  setAudioLabel(enabled: boolean): void {
    const button = this.root.querySelector<HTMLElement>('[data-action="toggle-audio"]');
    if (button) button.textContent = `音效：${enabled ? '开' : '关'}`;
  }

  setQualityLabel(high: boolean): void {
    const button = this.root.querySelector<HTMLElement>('[data-action="toggle-quality"]');
    if (button) button.textContent = `画质：${high ? '高' : '低'}`;
  }

  showDebug(data: Record<string, unknown> | null): void {
    const panel = this.get('debug-panel');
    panel.hidden = !data;
    if (data) panel.textContent = Object.entries(data).map(([key, value]) => `${key}: ${typeof value === 'number' ? value.toFixed(4) : String(value)}`).join('\n');
  }

  private get<T extends HTMLElement = HTMLElement>(id: string): T {
    const element = this.root.querySelector<T>(`#${id}`);
    if (!element) throw new Error(`UI element #${id} not found`);
    return element;
  }

  dispose(): void {
    clearTimeout(this.toastTimer);
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('submit', this.onSubmit);
    this.root.remove();
  }
}
